import React from 'react';
import {useLoaderData, useMatches} from '@remix-run/react';

//components
import {Link} from 'components/Link';
import Container from 'components/Container';
import BlogDetailsBreadcrumb from './BlogDetailsBreadcrumb';
import Module from 'components/modules/Module';
import BlogListingSidebar from '../blogListing/BlogListingSidebar';

//data
import {loader} from '~/routes/($lang)/blogs/$categoryHandle/$slug/index';
import type {I18nLocale} from '~/lib/type';

export default function BlogDetails() {
  const {
    currentBlog,
    nextBlog,
    previousBlog,
    blogSettings,
    translation,
    recentBlogs,
    tags,
    categories,
  } = useLoaderData<typeof loader>();
  const [root] = useMatches();
  const selectedLocale = root?.data?.selectedLocale as I18nLocale;
  const content =
    selectedLocale.language === 'EN'
      ? currentBlog?.page_sections_en ??
        currentBlog?.page_sections_zh_tw ??
        currentBlog?.page_sections
      : currentBlog?.page_sections_zh_tw ??
        currentBlog?.page_sections_en ??
        currentBlog?.page_sections;

  return (
    <section className="pb-20">
      <Container>
        <BlogDetailsBreadcrumb />
        <div className="grid gap-8 md:grid-cols-3">
          <div className="md:col-span-2">
            {/* The post's own heading; the reset in app.css gives h1 the
                same size and weight as the h3 it replaces. */}
            <h1 className="text-xl font-normal">{currentBlog?.blog_title}</h1>
            <p className="text-md md:text-lg !leading-snug font-normal mt-5 mb-8">
              <time dateTime={currentBlog.blog_published_date}>
                {currentBlog.blog_published_date}
              </time>
            </p>
            <div className="grid gap-13">
              {content
                ? content.map((module) => (
                    <Module key={module._key} module={module} />
                  ))
                : null}
            </div>
          </div>
          <BlogListingSidebar
            tags={tags}
            categories={categories}
            blogSettings={blogSettings}
            recentBlogs={recentBlogs}
          />
        </div>
        <div className="my-20">
          <div className={`flex items-center justify-center space-x-8`}>
            <div className="w-[132px]">
              {previousBlog && (
                <Link
                  to={`/blogs/${previousBlog.blog_category?.slug?.current}/${previousBlog?.slug?.current}`}
                  className="flex items-center space-x-2.5"
                >
                  <svg
                    width="42"
                    height="42"
                    viewBox="0 0 42 42"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle
                      r="21"
                      transform="matrix(1 0 0 -1 21 21)"
                      fill="#F29100"
                    />
                    <path
                      d="M23.0604 27.9684L23.7008 27.3596C23.8289 27.1994 23.8289 26.9431 23.7008 26.815L17.9052 21.0161L23.7008 15.1852C23.8289 15.057 23.8289 14.8007 23.7008 14.6406L23.0604 14.0318C22.9003 13.8716 22.6762 13.8716 22.5161 14.0318L15.7919 20.7278C15.6638 20.888 15.6638 21.1122 15.7919 21.2724L22.5161 27.9684C22.6762 28.1285 22.9003 28.1285 23.0604 27.9684Z"
                      fill="white"
                    />
                  </svg>
                  <div className="tracking-wide text-lg font-normal whitespace-nowrap">
                    {translation?.previous_page ?? 'PREVIOUS'}
                  </div>
                </Link>
              )}
            </div>
            <div className="w-[94px]">
              {nextBlog && (
                <Link
                  to={`/blogs/${nextBlog.blog_category?.slug?.current}/${nextBlog?.slug?.current}`}
                  className="flex items-center space-x-2.5"
                >
                  <div className="tracking-wide text-lg font-normal whitespace-nowrap">
                    {translation?.next_page ?? 'NEXT'}
                  </div>
                  <svg
                    width="42"
                    height="42"
                    viewBox="0 0 42 42"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="flex-shrink-0"
                  >
                    <circle cx="21" cy="21" r="21" fill="#F29100" />
                    <path
                      d="M19.4432 14.3304L18.8302 14.9131C18.7076 15.0664 18.7076 15.3117 18.8302 15.4344L24.3774 20.9847L18.8302 26.5658C18.7076 26.6884 18.7076 26.9337 18.8302 27.0871L19.4432 27.6697C19.5964 27.823 19.8109 27.823 19.9642 27.6697L26.4002 21.2607C26.5228 21.1074 26.5228 20.8927 26.4002 20.7394L19.9642 14.3304C19.8109 14.1771 19.5964 14.1771 19.4432 14.3304Z"
                      fill="white"
                    />
                  </svg>
                </Link>
              )}
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
}
